import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ThumbsUp, Smile, Frown, ShieldCheck, FolderCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { GermanDateInput } from "@/components/GermanDateInput";
import DocCheckAccessDialog from "@/components/DocCheckAccessDialog";
import ClientDocumentsPanel from "@/components/ClientDocumentsPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PHASE = {
  onboarding: ["Vertrag/TB, Onboarding", "bg-yellow-300"], laeuft: ["läuft", "bg-emerald-500"],
  beendet: ["erfolgreich beendet", "bg-orange-400"], abbruch: ["Abbruch / Problem", "bg-red-500"],
};
const SHY = {
  eignung: "TB Eignungs\u00ADfest\u00ADstellung", traeger: "Träger\u00ADbestätigung", anwesenheit: "Anwesen\u00ADheits\u00ADliste",
  bewertung: "Teilnehmer\u00ADbewertung", dokumentation: "Dokumen\u00ADtation", zwischenbericht: "Zwischen\u00ADbericht",
  abschlussbericht: "Abschluss\u00ADbericht", verbleib: "Verbleib (Job/ Ausbildung/ WB)",
};
const mmYYYY = (d) => (d ? `${d.slice(5, 7)}/${d.slice(0, 4)}` : "—");
const yearStart = () => `${new Date().getFullYear()}-01-01`;
const yearEnd = () => `${new Date().getFullYear()}-12-31`;

function Cell({ d, testId }) {
  if (!d.present && !d.due) return <td className="text-center text-slate-300 text-xs border-l border-slate-100" data-testid={testId}>–</td>;
  if (!d.present) return <td className="text-center bg-red-500/90 text-white text-[10px] font-medium border-l border-white" data-testid={testId}>fehlt</td>;
  const Icon = d.rating === "good" ? Smile : d.rating === "bad" ? Frown : ThumbsUp;
  return <td className="text-center border-l border-slate-100" title={d.info || ""} data-testid={testId}><Icon size={15} className={`inline ${d.rating === "bad" ? "text-red-500" : "text-emerald-600"}`} /></td>;
}

function Filters({ f, setF, measures }) {
  return (
    <div className="flex flex-wrap items-end gap-3 mb-6" data-testid="doc-filters">
      <label className="text-xs text-slate-600 flex flex-col">Von<GermanDateInput value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="mt-1 w-40" data-testid="doc-filter-start" /></label>
      <label className="text-xs text-slate-600 flex flex-col">Bis<GermanDateInput value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="mt-1 w-40" data-testid="doc-filter-end" /></label>
      <label className="text-xs text-slate-600 flex flex-col">Maßnahme
        <select value={f.measure} onChange={(e) => setF({ ...f, measure: e.target.value })} className="input-tech mt-1 !w-48" data-testid="doc-filter-measure">
          <option value="">Alle Maßnahmen</option>{measures.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </label>
      <label className="text-xs text-slate-600 flex flex-col">Status
        <select value={f.phase} onChange={(e) => setF({ ...f, phase: e.target.value })} className="input-tech mt-1 !w-44" data-testid="doc-filter-phase">
          <option value="">Alle</option>{Object.entries(PHASE).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
      <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Name suchen…" className="input-tech !w-48" data-testid="doc-filter-search" />
    </div>
  );
}

export default function UnterlagenPruefungPage() {
  const { user } = useAuth();
  const isAdmin = (user?.all_roles || [user?.role]).includes("admin") || !!user?.is_master_admin;
  const [denied, setDenied] = useState(false);
  const [openClient, setOpenClient] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [f, setF] = useState({ start: yearStart(), end: yearEnd(), measure: "", phase: "", q: "" });
  const [data, setData] = useState(null);
  const [access, setAccess] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams();
    if (f.start) p.set("start", f.start);
    if (f.end) p.set("end", f.end);
    if (f.measure) p.set("measure", f.measure);
    api.get(`/doc-check/overview?${p}`, { timeout: 60000 }).then((r) => setData(r.data))
      .catch((e) => (e.response?.status === 403 ? setDenied(true) : toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message)));
  }, [f.start, f.end, f.measure, reloadKey]);

  const rows = useMemo(() => (data?.rows || []).filter((r) => (!f.phase || r.phase === f.phase) && (!f.q || (r.name || "").toLowerCase().includes(f.q.toLowerCase()))), [data, f.phase, f.q]);
  const cols = useMemo(() => {
    if (!data) return {};
    return Object.fromEntries(data.slots.map((s) => {
      const due = rows.filter((r) => r.docs[s.key].due);
      const have = due.filter((r) => r.docs[s.key].present).length;
      return [s.key, due.length ? Math.round((have * 1000) / due.length) / 10 : null];
    }));
  }, [data, rows]);
  const total = useMemo(() => {
    if (!data) return null;
    let due = 0, have = 0;
    rows.forEach((r) => Object.values(r.docs).forEach((d) => { if (d.due) { due += 1; if (d.present) have += 1; } }));
    return due ? Math.round((have * 1000) / due) / 10 : null;
  }, [data, rows]);

  if (denied) return (
    <div className="max-w-xl mx-auto p-10 text-center" data-testid="doc-no-access">
      <div className="font-heading text-2xl font-light tracking-tighter text-slate-900 mb-2">Kein Zugriff</div>
      <p className="text-sm text-slate-500">Die Unterlagen-Prüfung ist nur für freigeschaltete Mitarbeiter verfügbar. Bitte wenden Sie sich an den Admin.</p>
    </div>
  );
  return (
    <div className="max-w-[1600px] mx-auto p-4 sm:p-8" data-testid="unterlagen-pruefung-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 flex items-center gap-1.5"><FolderCheck size={12} /> AVGS-Jobcoaching</div>
          <h1 className="font-heading text-3xl font-light tracking-tighter text-slate-900">Unterlagen-Prüfung</h1>
          <p className="text-sm text-slate-500 mt-1">Vollständigkeit der Teilnehmer-Unterlagen im gewählten Zeitraum.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right"><div className="font-heading text-4xl font-light tracking-tighter text-slate-900" data-testid="doc-total-pct">{total == null ? "—" : `${total.toLocaleString("de-DE")} %`}</div><div className="text-[11px] text-slate-500">gesamt vollständig</div></div>
          {isAdmin && <button onClick={() => setAccess(true)} className="px-4 py-2.5 text-xs font-medium border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid="doc-access-btn"><ShieldCheck size={12} /> Zugriff verwalten</button>}
        </div>
      </div>
      <Filters f={f} setF={setF} measures={data?.measures || []} />
      {!data ? <div className="text-xs text-slate-400">Lädt…</div> : (
        <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="doc-table-wrap">
          <table className="w-full text-sm min-w-[1250px] table-fixed" data-testid="doc-table">
            <thead>
              <tr className="bg-slate-50 text-[11px] text-red-600 align-bottom">
                <th className="px-3 py-3 text-left text-slate-700 sticky left-0 bg-slate-50 w-[200px]">Teilnehmer ({rows.length})</th>
                <th className="px-2 py-3 text-left w-[84px]">Start</th><th className="px-2 py-3 text-right w-[52px]">UE</th>
                {data.slots.map((s) => <th key={s.key} className="px-1.5 py-3 font-semibold leading-tight w-[88px] max-w-[88px] [overflow-wrap:normal] [hyphens:manual]">{SHY[s.key] || s.label}</th>)}
                <th className="px-2 py-3 text-slate-700 w-[70px]">Vollst.</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.client_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`doc-row-${r.client_id}`}>
                  <td className="px-3 py-2 sticky left-0 bg-white font-medium"><button onClick={() => setOpenClient(r)} className="hover:underline text-left" data-testid={`doc-open-${r.client_id}`}>{r.name}</button></td>
                  <td className="px-2 py-2"><span className={`inline-block px-2 py-0.5 text-[11px] font-mono text-slate-900 ${PHASE[r.phase][1]}`} title={PHASE[r.phase][0]}>{mmYYYY(r.start_date)}</span></td>
                  <td className="px-2 py-2 text-right font-mono text-xs">{r.total_ue ?? "—"}</td>
                  {data.slots.map((s) => <Cell key={s.key} d={r.docs[s.key]} testId={`doc-cell-${r.client_id}-${s.key}`} />)}
                  <td className="px-2 py-2 text-center font-mono text-xs" data-testid={`doc-row-pct-${r.client_id}`}>{r.completeness.toLocaleString("de-DE")} %</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={data.slots.length + 4} className="p-8 text-center text-slate-500" data-testid="doc-empty">Keine Teilnehmer im Zeitraum.</td></tr>}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50 text-xs font-mono">
                <td className="px-3 py-2 sticky left-0 bg-slate-50 font-sans font-bold text-slate-700" colSpan={3}>Vollständigkeit</td>
                {data.slots.map((s) => <td key={s.key} className="text-center py-2" data-testid={`doc-col-pct-${s.key}`}>{cols[s.key] == null ? "—" : `${cols[s.key].toLocaleString("de-DE")} %`}</td>)}
                <td className="text-center font-bold">{total == null ? "—" : `${total.toLocaleString("de-DE")} %`}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <div className="flex flex-wrap gap-4 mt-4 text-[11px] text-slate-600" data-testid="doc-legend">
        {Object.values(PHASE).map(([l, c]) => <span key={l} className="flex items-center gap-1.5"><span className={`w-3 h-3 ${c}`} />{l}</span>)}
        <span className="flex items-center gap-1.5"><ThumbsUp size={12} className="text-emerald-600" /> vorhanden</span>
        <span className="flex items-center gap-1.5"><span className="px-1 bg-red-500 text-white text-[10px]">fehlt</span> nicht vorhanden</span>
        <span className="flex items-center gap-1.5"><Smile size={12} className="text-emerald-600" /> gute Bewertung</span>
        <span className="flex items-center gap-1.5"><Frown size={12} className="text-red-500" /> mangelhafte Bewertung</span>
        <span className="text-slate-400">– noch nicht fällig</span>
      </div>
      <DocCheckAccessDialog open={access} onClose={() => setAccess(false)} />
      <Dialog open={!!openClient} onOpenChange={(o) => { if (!o) { setOpenClient(null); setReloadKey((k) => k + 1); } }}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto p-0" data-testid="doc-client-dialog">
          <DialogHeader className="px-5 pt-5"><DialogTitle>{openClient?.name}</DialogTitle>
            <Link to={`/avgs/${openClient?.client_id}?tab=unterlagen`} className="text-xs text-slate-500 hover:text-slate-900 underline" data-testid="doc-client-akte-link">Zur Kundenakte</Link>
          </DialogHeader>
          {openClient && <ClientDocumentsPanel clientId={openClient.client_id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
